import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo.jsx';
import { Button } from '../components/ui.jsx';

/**
 * The homepage.
 *
 * The headline is a claim the rest of the product has to keep, so the page
 * spends its space showing the claim rather than asserting it: a real terms
 * table, the actual fee, and the things we refuse to do.
 */
export default function Home() {
  return (
    <>
      <Seo
        title={null}
        description="The marketplace for creators and brands: partnership programs with clear terms, real audience stats, tracking and payouts in one place. No pay-to-play rankings, no vanity metrics."
      />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-2xs font-semibold uppercase tracking-widest text-accent">
              Influencer &amp; UGC marketplace
            </p>
            <h1 className="mt-3 text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl">
              Creators and brands.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ink-muted">
              Creators partner with brands in programs — sell, get paid, get gifts. Real stats and
              real money.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/signup?role=creator" size="lg">
                Join as a creator
              </Button>
              <Button to="/signup?role=brand" size="lg" variant="outline">
                List a program
              </Button>
              <Button to="/programs" size="lg" variant="ghost">
                Browse without an account →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Show, don't tell ────────────────────────────────────────────── */}
      <section className="border-b border-rule bg-paper-sunken">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">
                This is a program page, logged out.
              </h2>
              <p className="mt-3 text-ink-muted">
                Not a teaser. Not “competitive rates”. Not “apply to see terms”. The commission,
                the cookie window, the payout schedule and the content rules, on the page, for
                anyone.
              </p>
              <Link
                to="/programs"
                className="mt-4 inline-block text-sm font-medium text-accent hover:underline"
              >
                See the real directory →
              </Link>
            </div>

            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised shadow-card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th>Commission</th>
                    <th className="num">Cookie</th>
                    <th>Payout</th>
                    <th className="num">Conv. rate</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Skincare affiliate', '15% of sale', '30d', 'Net 30', '2.10%'],
                    ['UGC video package', '$450 per post', '—', 'Net 15', null],
                    ['App install bounty', '$12 per conversion', '7d', 'Monthly', '0.34%'],
                  ].map(([title, commission, cookie, payout, cvr]) => (
                    <tr key={title}>
                      <td className="font-medium">{title}</td>
                      <td className="font-semibold text-accent">{commission}</td>
                      <td className="num">{cookie}</td>
                      <td>{payout}</td>
                      <td className="num">
                        {cvr ?? <span className="unknown" title="Not enough traffic yet">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── The refusals ────────────────────────────────────────────────── */}
      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">What we don’t do</h2>
          <p className="mt-2 max-w-xl text-ink-muted">
            Most of what makes this marketplace usable is what it refuses to build.
          </p>

          <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: 'No hidden rates',
                body: 'There is no “apply to see the rate” mode, because a toggle for it would become the norm and the norm would be off.',
              },
              {
                title: 'No pay-to-play rankings',
                body: 'Boost buys a labelled slot above the results. It cannot move a single organic row — the ordering code never sees it.',
              },
              {
                title: 'No vanity metrics',
                body: 'Conversion rates show even when they’re bad. A metric with nothing to divide by shows a dash, not a flattering zero.',
              },
              {
                title: 'No laundered stats',
                body: 'Followers from a connected account are verified and dated. Anything typed in is labelled self-reported, next to the number, not in a footnote.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-paper-raised p-5">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section className="border-b border-rule bg-paper-sunken">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">How a partnership runs</h2>
          <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Apply or get invited', 'You already know the terms — they were on the page.'],
              ['Terms get frozen', 'The rate is copied at acceptance. Later edits never touch it.'],
              ['Traffic is tracked', 'A short link, deduplicated clicks, attribution inside the cookie window.'],
              ['Commissions accrue', 'Paid via Stripe, with Affily’s fee shown on every line.'],
            ].map(([title, body], i) => (
              <li key={title}>
                <span className="flex size-7 items-center justify-center rounded-full bg-ink text-xs font-semibold text-paper">
                  {i + 1}
                </span>
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1 text-sm text-ink-muted">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Pricing, in one line ────────────────────────────────────────── */}
      <section className="border-b border-rule">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">
            Free to join. A small fee on paid-out commissions.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-muted">
            No listing fees, no subscription to see rates, nothing on deals we didn’t track. Your
            rate is shown in your account before anything is paid.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button to="/signup?role=creator" size="lg">
              Join free
            </Button>
            <Button to="/transparency" size="lg" variant="outline">
              How transparency works
            </Button>
          </div>
        </div>
      </section>

      {/* ── Tools ───────────────────────────────────────────────────────── */}
      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight">Free tools, no signup</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                to: '/tools/engagement-rate-calculator',
                title: 'Engagement rate calculator',
                body: 'Work out your real engagement rate and see where it sits against typical ranges for your audience size.',
              },
              {
                to: '/tools/media-kit-generator',
                title: 'Media kit generator',
                body: 'Fill in a form, get a clean media kit you can download as a PDF and send to brands today.',
              },
            ].map((tool) => (
              <Link
                key={tool.to}
                to={tool.to}
                className="rounded-lg border border-rule bg-paper-raised p-5 transition-colors hover:border-accent"
              >
                <h3 className="font-semibold">{tool.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted">{tool.body}</p>
                <span className="mt-3 inline-block text-sm font-medium text-accent">Open →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
